import React, { useEffect, useRef, useState } from 'react'
import { format, parseISO } from 'date-fns'
import { navigateTo } from '../shared'
import { getRequest } from '../../services/api'
import { IAnomaliesV2SearchResponse, IAnomaliesV2SearchRow } from '../shared/types'

// Debounced typeahead. Hits /search after 300ms of idle typing and renders a
// dropdown of advertisers; click navigates to the per-advertiser overview.
// width: number = fixed pixel width. 'fill' = flex-grow inside parent.
export const AdvertiserSearch = (props: {
  width?: number | 'fill'
}) => {
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<IAnomaliesV2SearchRow[]>([])
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Close dropdown on outside click.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  // Debounced fetch.
  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length < 2) {
      setRows([])
      return
    }
    let cancelled = false
    const t = setTimeout(async () => {
      const response = await getRequest('/anomalies/v2/search', {
        q: trimmed,
        limit: 10,
      })
      if (cancelled) return
      const data = (response?.data || {}) as IAnomaliesV2SearchResponse
      setRows(data.rows || [])
      setOpen(true)
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [query])

  const handleSelect = (row: IAnomaliesV2SearchRow) => (e: React.MouseEvent) => {
    setOpen(false)
    setQuery('')
    // Unified detail page in advertiser-only mode — the backend resolves
    // sensible date / device / metric defaults.
    navigateTo(e, `/anomalies/${encodeURIComponent(row.advertiserDomain)}`)
  }

  return (
    <div
      ref={containerRef}
      className="anomalies-search"
      style={
        props.width === 'fill'
          ? { flex: 1, minWidth: 200 }
          : { width: props.width || 280 }
      }
    >
      <div className="control anomalies-search-control">
        <input
          type="text"
          placeholder="Search advertiser…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => rows.length > 0 && setOpen(true)}
        />
      </div>
      {open && rows.length > 0 && (
        <div className="anomalies-search-menu">
          {rows.map((row) => (
            <button
              key={row.advertiserDomain}
              type="button"
              className="anomalies-search-item"
              onClick={handleSelect(row)}
            >
              <div className="anomalies-search-item-main text-dark text-bold">
                {row.advertiserDomain}
              </div>
              <div className="anomalies-search-item-sub text-light text-small">
                {row.totalAlertDays} alert day
                {row.totalAlertDays === 1 ? '' : 's'}
                {row.latestAlertDate
                  ? ` · last ${format(parseISO(row.latestAlertDate), 'MMM d, yyyy')}`
                  : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
